// ─── Sélection d'opérations à rattacher : filtres et tri ──────────
// Spécification du 12/08, §5 à §7. Module PUR — six filtres qui se combinent
// en ET, six critères de tri, et une règle de visibilité qui prime sur tout.
//
// La règle qui prime (§6.4) : une opération DÉJÀ rattachée à la ligne courante
// reste visible quel que soit le filtre. Sans elle, elle deviendrait
// indétachable dès qu'on filtre — on l'aurait cochée sans pouvoir la décocher.
import { motifDeLibelle } from './releveBancaire.js';
import { rattachementsDeDepense, repartitionDepense, EPSILON } from './repartition.js';

const abs = (x) => Math.abs(Number(x) || 0);
const dateDe = (op) => String(op?.dateBanque || op?.datePaiement || op?.date || '');
const normaliser = (s) => String(s || '').toLowerCase().normalize('NFD').replace(/[̀-ͯ]/g, '');

// ── Valeurs par défaut (§6) ──────────────────────────────────────
// `sens` n'a pas de défaut fixe : il suit le sens de la ligne courante — un
// budget de charge s'ouvre sur « Dépenses ».
export const FILTRES_DEFAUT = {
  // 1 mois par défaut (Cédric, 13/08) : on part du plus proche et on élargit.
  // 0 = le mois de l'échéance et lui seul. On part du plus étroit et on
  // élargit : c'est là qu'une opération a le plus de chances de se trouver.
  periodeMois: 0, montant: '10', sens: null, comptes: null, rattachement: 'toutes',
  // `reste` : une operation entierement affectee ailleurs n'a PLUS RIEN a
  // donner. La proposer serait offrir un choix qui echouerait (§15 : part
  // sous le centime). Par defaut on ne montre donc que celles qui peuvent
  // encore servir ; 'toutes' les rend visibles pour comprendre pourquoi une
  // operation attendue n'apparait pas.
  reste: 'nonNul',   // 'nonNul' | 'toutes'
};

export const TOLERANCES = { exact: 0, 10: 0.10, 35: 0.35 };

// Un filtre est « actif » quand il s'écarte de son défaut : c'est ce que
// compte le badge du bouton (§4).
export function nbFiltresActifsRattachement(f = {}, sensDefaut = null) {
  const d = { ...FILTRES_DEFAUT, sens: sensDefaut };
  let n = 0;
  if ((f.periodeMois ?? d.periodeMois) !== d.periodeMois) n++;
  if ((f.montant ?? d.montant) !== d.montant) n++;
  if ((f.sens ?? d.sens) !== d.sens) n++;
  if (f.comptes && f.comptes.length) n++;
  if ((f.rattachement ?? d.rattachement) !== d.rattachement) n++;
  return n;
}

// ── Recherche (§5) ───────────────────────────────────────────────
// Porte sur le libellé ET le montant. « 252 », « 252,00 » et « 252.00 »
// doivent tous trouver. Indépendante du filtre Montant : les deux mécanismes
// ne se parlent pas, la spec l'exige.
export function correspondRecherche(op, q) {
  const requete = normaliser(q).trim();
  if (!requete) return true;
  if (normaliser(op?.libelle).includes(requete) || normaliser(op?.libelleBanque).includes(requete)) return true;
  const num = requete.replace(',', '.').replace(/[^0-9.]/g, '');
  if (!num) return false;
  const m = abs(op?.montant);
  return String(m).includes(num) || String(Math.round(m)).includes(num) || m.toFixed(2).includes(num);
}

// ── Filtres (§6) ─────────────────────────────────────────────────
/**
 * `reference` : montant de l'échéance visée — c'est LUI qui sert de pivot au
 * filtre Montant (§6.2), et la comparaison porte sur le montant TOTAL de
 * l'opération, jamais sur ce qu'il en reste. Sinon une opération dont il ne
 * resterait que 25 % sortirait de la fenêtre et deviendrait introuvable.
 */
export function filtrerCandidates({
  operations = [], rattachements = [], ligneId, mois = null, filtres = {}, recherche = '',
  reference = 0, aujourdhui = null,
}) {
  const f = { ...FILTRES_DEFAUT, ...filtres };
  // §6.4 — les opérations déjà rattachées échappent aux filtres, sinon elles
  // deviendraient indétachables. Mais « déjà rattachée » à QUOI ?
  // • sans `mois` (rattachement en LOT sur une prévision) : à la LIGNE, tous
  //   mois confondus — c'est ce que la sélection multiple manipule ;
  // • avec `mois` (une occurrence précise) : à CE MOIS seulement.
  // Sans cette distinction, une opération rattachée à un AUTRE mois de la même
  // prévision traversait le filtre « non rattachées » pour s'afficher à
  // 0,00 € — plus rien à donner, et pourtant listée (Cédric, 14/08).
  const dejaSurCetteLigne = new Set(
    rattachements
      .filter(r => r?.ligneId === ligneId && (!mois || r.mois === mois))
      .map(r => r.depenseId),
  );

  // La période est une FENÊTRE autour du mois visé, pas un horizon depuis
  // aujourd'hui : sur une échéance de février, partir de la date du jour n'a
  // aucun rapport avec ce qu'on regarde (Cédric, 14/08).
  // `n` = nombre de mois de part et d'autre. 0 = ce mois seul.
  // Sans `mois` (rattachement en lot), on retombe sur le mois courant.
  const fenetre = (() => {
    if (f.periodeMois === 'tout') return null;
    const n = Math.max(0, Number(f.periodeMois) || 0);
    const ref = /^\d{4}-\d{2}$/.test(mois || '')
      ? mois
      : String(aujourdhui || new Date().toISOString().slice(0, 10)).slice(0, 7);
    const [y, m] = ref.split('-').map(Number);
    const debut = new Date(Date.UTC(y, m - 1 - n, 1));
    const fin = new Date(Date.UTC(y, m + n, 0));     // dernier jour du mois + n
    return { debut: debut.toISOString().slice(0, 10), fin: fin.toISOString().slice(0, 10) };
  })();

  return operations.filter(op => {
    if (!op) return false;
    // §6.4 — visible quoi qu'il arrive, sinon indétachable.
    if (dejaSurCetteLigne.has(op.id)) return true;
    if (op.exclu) return false;              // un virement interne ne se rattache pas

    if (fenetre) {
      const d = dateDe(op);
      if (d < fenetre.debut || d > fenetre.fin) return false;
    }

    if (f.sens === 'depense' && op.sens === 'revenu') return false;
    if (f.sens === 'revenu' && op.sens !== 'revenu') return false;

    if (f.comptes && f.comptes.length && !f.comptes.includes(op.compteId || null)) return false;

    if (f.montant !== 'tous' && reference > 0) {
      const tol = TOLERANCES[f.montant] ?? 0.10;
      const ecart = Math.abs(abs(op.montant) - abs(reference)) / abs(reference);
      if (ecart > tol + 1e-9) return false;
    }

    if (f.reste === 'nonNul' && repartitionDepense(op, rattachements).reste <= EPSILON) return false;

    if (f.rattachement !== 'toutes') {
      const { affecte } = repartitionDepense(op, rattachements);
      const rattachee = affecte > EPSILON;
      if (f.rattachement === 'non' && rattachee) return false;
      if (f.rattachement === 'oui' && !rattachee) return false;
    }

    return true;
  })
  // La RECHERCHE s'applique à tout le monde, y compris aux opérations déjà
  // rattachées : §6.4 les protège des FILTRES, pas d'une recherche explicite.
  // Chercher « EDF » ne doit pas faire apparaître un prélèvement SFR sous
  // prétexte qu'il est déjà lié.
  .filter(op => correspondRecherche(op, recherche));
}

// ── Tri (§7) ─────────────────────────────────────────────────────
export const TRIS_RATTACHEMENT = [
  { id: 'date',    cle: 'finances.triDate' },
  { id: 'montant', cle: 'finances.triMontantOp' },
  { id: 'libelle', cle: 'finances.tri_libelle' },
  { id: 'compte',  cle: 'finances.comptes' },
];

// `sens` : -1 décroissant (défaut pour date et montant), +1 croissant.
export function trierCandidates(operations = [], { tri = 'date', sens = -1, comptes = [] } = {}) {
  const nomCompte = (id) => (comptes.find(c => c?.id === id) || {}).nom || '';
  const out = [...operations];
  out.sort((a, b) => {
    let d = 0;
    if (tri === 'montant') d = abs(a.montant) - abs(b.montant);
    else if (tri === 'libelle') d = String(a.libelle || '').localeCompare(String(b.libelle || ''));
    else if (tri === 'compte') d = nomCompte(a.compteId).localeCompare(nomCompte(b.compteId));
    else d = String(dateDe(a)).localeCompare(String(dateDe(b)));
    // Départage stable par date puis par identifiant : sans lui, deux
    // opérations de même montant changeraient d'ordre à chaque rendu.
    if (d === 0) d = String(dateDe(a)).localeCompare(String(dateDe(b)));
    if (d === 0) d = String(a.id).localeCompare(String(b.id));
    return sens < 0 ? -d : d;
  });
  return out;
}

// ── Sélection (§11) ──────────────────────────────────────────────
// Stockée par IDENTIFIANT, jamais par index : elle doit survivre aux
// changements de filtre, de tri et de recherche.
export const selectionInitiale = (rattachements = [], ligneId) =>
  new Set(rattachements.filter(r => r?.ligneId === ligneId).map(r => r.depenseId));

// Le diff à écrire (§11) : ce qu'on ajoute, ce qu'on retire.
export function diffSelection(initiale, courante) {
  const ajouts = [...courante].filter(id => !initiale.has(id));
  const retraits = [...initiale].filter(id => !courante.has(id));
  return { ajouts, retraits, modifie: ajouts.length > 0 || retraits.length > 0 };
}

// Le mois par défaut d'une affectation (§9) : celui de la date de l'opération.
// Jamais recalculé ensuite (§9.2) — un choix manuel ne doit pas être écrasé.
export const moisParDefaut = (op) => dateDe(op).slice(0, 7);

// Une opération est-elle « hors plage » de la ligne (§9.1) ? Elle reste
// visible et sélectionnable, mais la sauvegarde attend un mois explicite.
export const horsPlage = (op, moisDus = []) =>
  !moisDus.includes(moisParDefaut(op));

// Le motif d'une opération, pour reconnaître un libellé déjà vu.
export const motifOperation = (op) => motifDeLibelle(op?.libelleBanque || op?.libelle);
